import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { persistor, useAppDispatch } from 'store';
import {
  changeAccountsListSearchCondition,
  changeAccountsPageAndSize,
  changeCmnPageAndSize,
  changePageAndSize,
  changeProgressInfo,
  changeProjectCreateStatus,
  changeProjectListSearchCondition,
  changeProjectListSubSearchCondition,
  changeSurveyResult,
  changeUrlPartCode,
  changeUrlProjectId,
  changeProjectName,
  projectSelector,
  changeSurveyInfoResult,
} from 'store/project/projectSlice';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { ICmnSWRCustomInfo, ICmnSWRInfo, ICommonInfiniteProps, Order } from 'interface/cmn/commonInfo';
import {
  IProjectListProps,
  IProjectState,
  IProjectListSearchCondition,
  ICommCodeProps,
  ICommCodeGroupPTProps,
  ICompAccountsProps,
  IAccountsListSearchCondition,
  SearchConditionKeys,
  ISurveyListLatestProps,
  IContent,
  IInspectionDetailProps,
  IProjectUsersListProps,
  AccountsContent,
  IPostSurveyResultListProps,
  IProjectDoneProps,
} from 'interface/project/projectInfo';
import isEmpty from 'lodash/isEmpty';
import { useNavigate } from 'react-router-dom';
import { SHOW_PROJECT_USERS_FROM_DATE_FILTER } from 'constants/features';
import useLogin from './useLogin';
import { fetcherHeaderWithParam, fetcherHeaderWithParamHandling } from './useRequest';

export type ProjectOperators = {
  getProjectList: () => ICmnSWRInfo<IProjectListProps>;
  getCommCodes: () => ICmnSWRInfo<ICommCodeProps[]>;
  getCommCodeGroup: () => ICmnSWRInfo<ICommCodeGroupPTProps>;
  getCompAccounts: () => ICmnSWRInfo<ICompAccountsProps>;
  getInfiniteSurveyListLatest: () => ICommonInfiniteProps<ISurveyListLatestProps>;
  getInfiniteSurveyListByName: (_id: string) => ICommonInfiniteProps<ISurveyListLatestProps>;
  getProjectIdDetail: (_projectId: string) => ICmnSWRInfo<IContent>;
  getInspectionDetail: (_inspectionId: string) => ICmnSWRInfo<IInspectionDetailProps>;
  getProjectUsersList: (_projectId: string, _fromDate?: string) => ICmnSWRInfo<IProjectUsersListProps>;
  getProjectListByAccount: (_id: string) => ICmnSWRInfo<IProjectListProps>;
  getCompBizAdminInfo: (_id: string) => ICmnSWRInfo<AccountsContent>;
  getAdminAccountProfile: () => ICmnSWRCustomInfo<AccountsContent>;
  changeProjectListSearchCondition: (_projectListSearchCondition: IProjectListSearchCondition) => void;
  changePageAndSize: (_page: number, _size: number) => void;
  changeProjectListSubSearchCondition: (_order: Order, _orderBy: string) => void;
  changeAccountsListSearchCondition: (_accountsListSearchCondition: IAccountsListSearchCondition) => void;
  changeAccountsPageAndSize: (_page: number, _size: number) => void;
  changeProjectCreateStatus: (_isProjectCreate: boolean) => void;
  changeCmnPageAndSize: (_key: SearchConditionKeys, _page: number, _size: number) => void;
  changeUrlProjectId: (_urlProjectId: string) => void;
  changeUrlPartCode: (_urlPartCode: string) => void;
  changeProgressInfo: (_progressInfo: number) => void;
  changeSurveyResult: (_obj: IPostSurveyResultListProps) => void;
  changeProjectName: (_projectName: string) => void;
  changeSurveyInfoResult: (_obj: IPostSurveyResultListProps) => void;
} & IProjectState;

export function useProjectDone(projectId: string, partCode: string): ICmnSWRInfo<IProjectDoneProps> {
  const url = process.env.REACT_APP_URL;
  const { info } = useLogin();
  const loginToken = info.accessToken || '';
  const { data, error, mutate } = useSWR(
    [`${url}/api/project/done/${projectId}/part-code/${partCode}`, loginToken],
    ([_url, _loginToken]) => fetcherHeaderWithParam(_url || '', _loginToken),
  );
  return { data: data?.data, isLoading: !error && !data, isError: error, mutate, duration: data?.duration || 0 };
}

const getProjectUsersFromDateCookie = (projectId: string): string => {
  if (!projectId) return '';
  const cookieKey = `project-users-from-date-${projectId}`;
  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${cookieKey}=`))
    ?.split('=')[1];
  return cookie ? decodeURIComponent(cookie) : '';
};

function useProjectList(): ICmnSWRInfo<IProjectListProps> {
  const url = process.env.REACT_APP_URL;
  const { info, logout } = useLogin();
  const navigate = useNavigate();
  const loginToken = info.accessToken || '';
  const { projectListSearchCondition } = useSelector(projectSelector);
  const { page, size, compName, adminName } = projectListSearchCondition;

  const { data, error, mutate } = useSWR(
    () => [`${url}/api/project/list`, loginToken, page, size, compName, adminName],
    ([_url, _accessToken, _page, _size, _compName, _adminName]) =>
      fetcherHeaderWithParam(_url || '', _accessToken, {
        page: _page,
        size: _size,
        compName: _compName,
        adminName: _adminName,
      }),
  );
  const handleLogout = async () => {
    await logout();
    await persistor.purge();
    navigate('/login');
  };
  if (error && error?.response?.status === 401) {
    handleLogout();
  }
  return {
    data: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    duration: data?.duration || 0,
  };
}

function useProjectCommCodes(): ICmnSWRInfo<ICommCodeProps[]> {
  const url = process.env.REACT_APP_URL;
  const { info } = useLogin();
  const loginToken = info.accessToken || '';

  const { data, error, mutate } = useSWR(
    () => [`${url}/api/comm/codes`, loginToken],
    ([_url, _accessToken]) => fetcherHeaderWithParam(_url || '', _accessToken),
  );
  return {
    data: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    duration: data?.duration || 0,
  };
}

function useProjectCommCode(): ICmnSWRInfo<ICommCodeGroupPTProps> {
  const url = process.env.REACT_APP_URL;
  const { info } = useLogin();
  const loginToken = info.accessToken || '';

  const { data, error, mutate } = useSWR(
    () => [`${url}/api/comm/code`, loginToken],
    ([_url, _accessToken]) =>
      fetcherHeaderWithParam(_url || '', _accessToken, {
        group: 'PT',
      }),
  );
  return {
    data: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    duration: data?.duration || 0,
  };
}
function useProjectCompAccounts(): ICmnSWRInfo<ICompAccountsProps> {
  const url = process.env.REACT_APP_URL;
  const { info } = useLogin();
  const loginToken = info.accessToken || '';

  const { accountsListSearchCondition } = useSelector(projectSelector);
  const { page, size, name, compName } = accountsListSearchCondition;

  const { data, error, mutate } = useSWR(
    () => [`${url}/api/comp/accounts`, loginToken, page, size, name, compName],
    ([_url, _accessToken, _page, _size, _name, _compName]) =>
      fetcherHeaderWithParam(_url || '', _accessToken, {
        page: _page,
        size: _size,
        name: _name,
        compName: _compName,
      }),
  );
  return {
    data: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    duration: data?.duration || 0,
  };
}

function useInfiniteSurveyListLatest() {
  const url = process.env.REACT_APP_URL;
  const { info } = useLogin();
  const loginToken = info.accessToken || '';
  const ROWS = 30;
  const getKey = (pageIndex: number): [string, string, number, number] => {
    return [`${url}/api/survey/listLatest`, loginToken, pageIndex, ROWS];
  };

  const { data, size, setSize, mutate, error, isLoading } = useSWRInfinite(
    getKey,
    async ([_url, _accessToken, _page, _size]): Promise<ISurveyListLatestProps> => {
      const response = await fetcherHeaderWithParam(_url || '', _accessToken, {
        page: _page,
        size: _size,
      });
      return response.data;
    },
  );

  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (size > 0 && data && typeof data?.[size - 1] === 'undefined');
  const isEmptyData = data?.[0]?.contents?.length === 0;
  const isReachingEnd = isEmptyData || (data && data[data.length - 1]?.contents?.length < ROWS);
  return {
    data,
    size,
    setSize,
    isLoadingMore,
    isReachingEnd,
    mutate,
    isLoading,
  };
}

function useProjectIdDetail(_projectId: string): ICmnSWRInfo<IContent> {
  const url = process.env.REACT_APP_URL;
  const { info } = useLogin();
  const loginToken = info.accessToken || '';

  const { data, error, mutate } = useSWR(
    !isEmpty(_projectId) ? [`${url}/api/project/${_projectId}/detail`, loginToken] : null,
    ([_url, _accessToken]) => fetcherHeaderWithParam(_url || '', _accessToken),
  );
  return {
    data: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    duration: data?.duration || 0,
  };
}

export function useProjectIdDetailPartCode(projectId: string, participationCode: string): ICmnSWRInfo<IContent> {
  const url = process.env.REACT_APP_URL;
  const { info } = useLogin();
  const loginToken = info.accessToken || '';
  const { data, error, mutate } = useSWR(
    !isEmpty(projectId) && !isEmpty(participationCode)
      ? [`${url}/api/project/${projectId}/detail/${participationCode}`, loginToken]
      : null,
    ([_url, _loginToken]) => fetcherHeaderWithParam(_url || '', _loginToken),
  );
  return { data: data?.data, isLoading: !error && !data, isError: error, mutate, duration: data?.duration || 0 };
}

function useIInspectionDetail(_inspectionId: string): ICmnSWRInfo<IInspectionDetailProps> {
  const url = process.env.REACT_APP_URL;
  const { info } = useLogin();
  const loginToken = info.accessToken || '';

  const { data, error, mutate } = useSWR(
    !isEmpty(_inspectionId) ? [`${url}/api/inspection/${_inspectionId}/ipsative/info`, loginToken] : null,
    ([_url, _accessToken]) => fetcherHeaderWithParam(_url || '', _accessToken),
  );
  return {
    data: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    duration: data?.duration || 0,
  };
}

function useProjectUsersList(_projectId: string, _fromDate?: string): ICmnSWRInfo<IProjectUsersListProps> {
  const url = process.env.REACT_APP_URL;
  const { info } = useLogin();
  const loginToken = info.accessToken || '';
  const { projectUsersListSearchCondition } = useSelector(projectSelector);
  const { page, size } = projectUsersListSearchCondition;
  const fromDate = SHOW_PROJECT_USERS_FROM_DATE_FILTER ? _fromDate ?? getProjectUsersFromDateCookie(_projectId) : '';

  const { data, error, mutate } = useSWR(
    !isEmpty(_projectId) ? [`${url}/api/project/${_projectId}/users/list`, loginToken, page, size, fromDate] : null,
    ([_url, _accessToken, _page, _size, _fromDate]) =>
      fetcherHeaderWithParam(_url || '', _accessToken, {
        page: _page,
        size: _size,
        fromDate: _fromDate || undefined,
      }),
  );
  return {
    data: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    duration: data?.duration || 0,
  };
}

function useProjectListByAccount(id: string): ICmnSWRInfo<IProjectListProps> {
  const url = process.env.REACT_APP_URL;
  const { info } = useLogin();
  const loginToken = info.accessToken || '';
  const { projectListSearchCondition } = useSelector(projectSelector);
  const { page, size, compName, adminName } = projectListSearchCondition;

  const { data, error, mutate } = useSWR(
    !isEmpty(id) ? [`${url}/api/project/list/${id}`, loginToken, page, size, compName, adminName] : null,
    ([_url, _accessToken, _page, _size, _compName, _adminName]) =>
      fetcherHeaderWithParam(_url || '', _accessToken, {
        page: _page,
        size: _size,
        compName: _compName,
        adminName: _adminName,
      }),
  );

  return {
    data: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    duration: data?.duration || 0,
  };
}

function useCompBizAdminInfo(id: string): ICmnSWRInfo<AccountsContent> {
  const url = process.env.REACT_APP_URL;
  const { info } = useLogin();
  const loginToken = info.accessToken || '';

  const { data, error, mutate } = useSWR(
    !isEmpty(id) ? [`${url}/api/comp/biz/admin/${id}/info`, loginToken] : null,
    ([_url, _accessToken]) => fetcherHeaderWithParam(_url || '', _accessToken),
  );

  return {
    data: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    duration: data?.duration || 0,
  };
}

function useInfiniteSurveyListByName(id: string) {
  const url = process.env.REACT_APP_URL;
  const { info } = useLogin();
  const loginToken = info.accessToken || '';
  const ROWS = 30;
  const getKey = (pageIndex: number): [string, string, number, number] | null => {
    if (!id) return null;
    return [`${url}/api/survey/${id}/listByName`, loginToken, pageIndex, ROWS];
  };

  const { data, size, setSize, mutate, error, isLoading } = useSWRInfinite(
    getKey,
    async ([_url, _accessToken, _page, _size]): Promise<ISurveyListLatestProps> => {
      const response = await fetcherHeaderWithParam(_url || '', _accessToken, {
        page: _page,
        size: _size,
      });
      return response.data;
    },
  );

  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (size > 0 && data && typeof data?.[size - 1] === 'undefined');
  const isEmptyData = data?.[0]?.contents?.length === 0;
  const isReachingEnd = isEmptyData || (data && data[data.length - 1]?.contents?.length < ROWS);
  return {
    data,
    size,
    setSize,
    isLoadingMore,
    isReachingEnd,
    mutate,
    isLoading,
  };
}

function useAdminAccountProfile(): ICmnSWRCustomInfo<AccountsContent> {
  const url = process.env.REACT_APP_URL;
  const { info } = useLogin();
  const loginToken = info.accessToken || '';

  const { data, error, mutate } = useSWR(
    !isEmpty(loginToken) ? [`${url}/api/admin/account/profile`, loginToken] : null,
    ([_url, _accessToken]) => fetcherHeaderWithParamHandling(_url || '', _accessToken),
  );

  return {
    data: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
}

export const useProject = (): Readonly<ProjectOperators> => {
  const dispatch = useAppDispatch();
  const projectState = useSelector(projectSelector);
  return {
    ...projectState,
    getProjectList: useProjectList,
    getCommCodes: useProjectCommCodes,
    getCommCodeGroup: useProjectCommCode,
    getCompAccounts: useProjectCompAccounts,
    getInfiniteSurveyListLatest: useInfiniteSurveyListLatest,
    getInfiniteSurveyListByName: useInfiniteSurveyListByName,
    getProjectIdDetail: useProjectIdDetail,
    getInspectionDetail: useIInspectionDetail,
    getProjectUsersList: useProjectUsersList,
    getProjectListByAccount: useProjectListByAccount,
    getCompBizAdminInfo: useCompBizAdminInfo,
    getAdminAccountProfile: useAdminAccountProfile,
    changeProjectListSearchCondition: useCallback(
      (_projectListSearchCondition: IProjectListSearchCondition) => {
        dispatch(
          changeProjectListSearchCondition({
            projectListSearchCondition: _projectListSearchCondition,
          }),
        );
      },
      [dispatch],
    ),
    changePageAndSize: useCallback(
      (_page: number, _size: number) => {
        dispatch(
          changePageAndSize({
            page: _page,
            size: _size,
          }),
        );
      },
      [dispatch],
    ),
    changeProjectListSubSearchCondition: useCallback(
      (_order: Order, _orderBy: string) => {
        dispatch(
          changeProjectListSubSearchCondition({
            order: _order,
            orderBy: _orderBy,
          }),
        );
      },
      [dispatch],
    ),

    changeAccountsListSearchCondition: useCallback(
      (_accountsListSearchCondition: IAccountsListSearchCondition) => {
        dispatch(
          changeAccountsListSearchCondition({
            accountsListSearchCondition: _accountsListSearchCondition,
          }),
        );
      },
      [dispatch],
    ),
    changeAccountsPageAndSize: useCallback(
      (_page: number, _size: number) => {
        dispatch(
          changeAccountsPageAndSize({
            page: _page,
            size: _size,
          }),
        );
      },
      [dispatch],
    ),
    changeProjectCreateStatus: useCallback(
      (_isProjectCreate: boolean) => {
        dispatch(
          changeProjectCreateStatus({
            isProjectCreate: _isProjectCreate,
          }),
        );
      },
      [dispatch],
    ),
    changeCmnPageAndSize: useCallback(
      (_key: SearchConditionKeys, _page: number, _size: number) => {
        dispatch(
          changeCmnPageAndSize({
            key: _key,
            page: _page,
            size: _size,
          }),
        );
      },
      [dispatch],
    ),
    changeUrlProjectId: useCallback(
      (_urlProjectId: string) => {
        dispatch(changeUrlProjectId({ urlProjectId: _urlProjectId }));
      },
      [dispatch],
    ),
    changeUrlPartCode: useCallback(
      (_urlPartCode: string) => {
        dispatch(changeUrlPartCode({ urlPartCode: _urlPartCode }));
      },
      [dispatch],
    ),
    changeProgressInfo: useCallback(
      (_progressInfo: number) => {
        dispatch(changeProgressInfo({ progressInfo: _progressInfo }));
      },
      [dispatch],
    ),
    changeSurveyResult: useCallback(
      (_obj: IPostSurveyResultListProps) => {
        dispatch(
          changeSurveyResult({
            ..._obj,
          }),
        );
      },
      [dispatch],
    ),
    changeSurveyInfoResult: useCallback(
      (_obj: IPostSurveyResultListProps) => {
        dispatch(
          changeSurveyInfoResult({
            ..._obj,
          }),
        );
      },
      [dispatch],
    ),
    changeProjectName: useCallback(
      (_projectName: string) => {
        dispatch(changeProjectName({ projectName: _projectName }));
      },
      [dispatch],
    ),
  };
};
